import { Box, Divider, Stack } from '@chakra-ui/react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useCallback } from 'react';
import LoadingSpinner from 'components/LoadingSpinner';
import { FormBodyProps } from 'types/common';
import Input from 'components/Form/Input';
import DatePicker from 'components/Form/Date';
import Select from 'components/Form/Select';
import {
  LOYALTY_ACTIVITY_DEFINITION_TYPE,
  LOYALTY_ACTIVITY_DEFINITION_TYPE_OPTIONS,
  PRIZE_TYPE,
  PRIZE_TYPE_OPTIONS,
  TH_TRANSACTION_SOURCE_OPTIONS,
} from 'pages/LoyaltyActivityDefinitions/Form/constants';
import TagInput from 'components/Form/TagInput';
import Switch from 'components/Form/Switch';

function FormBody({ isLoading }: FormBodyProps) {
  const { control, setValue } = useFormContext();
  const [type, prizeType] = useWatch({ control, name: ['type', 'prizeType'] });

  const handleTypeChange = useCallback(
    (newValue: LOYALTY_ACTIVITY_DEFINITION_TYPE) => {
      if (
        [
          LOYALTY_ACTIVITY_DEFINITION_TYPE.ORDER_GROSS_AMOUNT,
          LOYALTY_ACTIVITY_DEFINITION_TYPE.JOINED_LOYALTY_PROGRAM,
          LOYALTY_ACTIVITY_DEFINITION_TYPE.ORDER_SOURCE,
        ].includes(newValue)
      ) {
        setValue('allowedSkus', []);
      }

      if (
        [
          LOYALTY_ACTIVITY_DEFINITION_TYPE.PRODUCTS_ONLY_ONCE,
          LOYALTY_ACTIVITY_DEFINITION_TYPE.PRODUCTS_PER_ITEM,
          LOYALTY_ACTIVITY_DEFINITION_TYPE.JOINED_LOYALTY_PROGRAM,
        ].includes(newValue)
      ) {
        setValue('minOrderGrossAmount', 0);
      }

      if (newValue !== LOYALTY_ACTIVITY_DEFINITION_TYPE.ORDER_SOURCE) {
        setValue('allowedSources', []);
      }
    },
    [setValue],
  );

  return (
    <Box w="100%">
      {isLoading && <LoadingSpinner />}
      <Stack spacing={4}>
        <Input name="name" label="Nazwa" placeholder="Wpisz nazwę" />

        <DatePicker
          name="activeFrom"
          label="Aktywna od"
          showError
          showTimeInput
        />

        <DatePicker
          name="activeTo"
          label="Aktywna do"
          showError
          showTimeInput
        />

        <Switch name="isOneTime" label="Czy aktywność jednorazowa?" />

        <Divider />

        <Select
          name="type"
          label="Typ"
          placeholder="Wybierz typ"
          options={LOYALTY_ACTIVITY_DEFINITION_TYPE_OPTIONS}
          onChangeCallback={handleTypeChange}
        />

        {[
          LOYALTY_ACTIVITY_DEFINITION_TYPE.PRODUCTS_ONLY_ONCE,
          LOYALTY_ACTIVITY_DEFINITION_TYPE.PRODUCTS_PER_ITEM,
        ].includes(type) && (
          <TagInput
            name="allowedSkus"
            label="Dozwolone SKU"
            placeholder="Wpisz SKU"
          />
        )}

        {type === LOYALTY_ACTIVITY_DEFINITION_TYPE.ORDER_SOURCE && (
          <Select
            name="allowedSources"
            label="Dozwolone źródła"
            placeholder="Wybierz źródła"
            options={TH_TRANSACTION_SOURCE_OPTIONS}
            isMulti
          />
        )}

        {[
          LOYALTY_ACTIVITY_DEFINITION_TYPE.ORDER_SOURCE,
          LOYALTY_ACTIVITY_DEFINITION_TYPE.ORDER_GROSS_AMOUNT,
        ].includes(type) && (
          <Input
            name="minOrderGrossAmount"
            label="Minimalna wartość zamówienia"
            placeholder="Wpisz minimalną wartość zamówienia"
            type="number"
            step={0.01}
          />
        )}

        <Divider />

        <Select
          name="prizeType"
          label="Typ nagrody"
          placeholder="Wybierz typ nagrody"
          options={PRIZE_TYPE_OPTIONS}
        />

        {prizeType === PRIZE_TYPE.POINTS && (
          <>
            <Input
              name="points"
              label="Punkty"
              placeholder="Wpisz punkty"
              type="number"
            />
            <Input
              name="pointsValidityInDays"
              label="Ważność punktów (w dniach)"
              placeholder="Wpisz liczbę dni"
              type="number"
            />
            <Switch
              name="prizeAvailableImmediately"
              label="Nagroda dostępna od razu"
            />
          </>
        )}

        {prizeType === PRIZE_TYPE.TRANSACTIONS_VALIDITY_EXTENSION && (
          <Input
            name="transactionsValidityExtensionInDays"
            label="Przedłużenie ważności transakcji (w dniach)"
            placeholder="Wpisz liczbę dni"
            type="number"
          />
        )}
      </Stack>
    </Box>
  );
}

export default FormBody;
